<script lang="ts">
  /**
   * SetRibbonSection — Swim-tab mount for the set ribbon (IA increment 1).
   * Reads the shared selected-swim store (currentDerivedWorkout, written by
   * Prototypes after every firehose fetch) so the ribbon follows whatever
   * swim the user selected, not just the latest. Renders NOTHING while the
   * store is null (loading/error) or when the swim has no set data — no
   * placeholder, no orphan header, so there's no empty-flash on load.
   */
  import SetRibbon from "./SetRibbon.svelte";
  import SetRecognitionCallout from "./SetRecognitionCallout.svelte";
  import { onMount } from "svelte";
  import { get } from "svelte/store";
  import { currentDerivedWorkout, currentRawFirehose } from "../lib/state";
  import { registerVisibleSets, visibleSetsOf } from "../lib/club/visibleSets";
  import { ribbonHeader, ribbonCaption } from "../lib/openWaterCopy";

  // `showcase` (default false) is the PUBLIC-page flag. When true, the owner-
  // personal first-time-ever recognition callout is suppressed: "first time"
  // is meaningless to a stranger, it lumps Warmup/Cooldown in with the real
  // signature sets, and its copy can disagree with the ribbon's own labels.
  // The signature sets are told through the ribbon labels themselves. Everything
  // else (header, ribbon) renders unchanged. Production callers pass nothing.
  let { showcase = false }: { showcase?: boolean } = $props();

  // Build 16 parity proof: the owner's side of `window.__swimsetDumpVisibleSets()`.
  onMount(() =>
    registerVisibleSets(() => ({
      screen: "sets-tab",
      swimId: get(currentRawFirehose)?.workout.uuid ?? null,
      sets: visibleSetsOf(get(currentDerivedWorkout)?.setGroups ?? []),
    })),
  );

  // VP gate note (post-7e51dee), updated for increment 2: the date anchor
  // now lives in SwimHeroCard directly above, fed by the same lockstep
  // stores, so hero date and ribbon can never disagree. The COUNT stays
  // here so a clipped last set can't read as fewer sets.
  //
  // setCount is simply allGroups.length — every set group, bookends included —
  // so the header always matches what actually renders in the ribbon below.
  let allGroups = $derived($currentDerivedWorkout?.setGroups ?? []);
  let setCount = $derived(allGroups.length);
  // Open water is one continuous swim shown as splits — count the splits (laps),
  // not the single whole-swim group, and use split vocabulary. Pool swims keep
  // the exact "N set(s)" subline they shipped with.
  let isOpenWater = $derived($currentDerivedWorkout?.isOpenWater === true);
  let splitCount = $derived($currentDerivedWorkout?.laps.length ?? 0);
  let subline = $derived(
    isOpenWater
      ? `${splitCount} ${splitCount === 1 ? "split" : "splits"}`
      : `${setCount} ${setCount === 1 ? "set" : "sets"}`,
  );
</script>

{#if $currentDerivedWorkout && $currentDerivedWorkout.setGroups.length > 0}
  <div class="ribbon-host">
    <header class="hdr">
      <h2>{ribbonHeader(isOpenWater, showcase)}</h2>
      <p class="sub">{subline}</p>
    </header>
    <p class="caption">{ribbonCaption(isOpenWater)}</p>
    {#if !showcase}
      <SetRecognitionCallout />
    {/if}
    <SetRibbon dw={$currentDerivedWorkout} {showcase} />
  </div>
{/if}

<style>
  .ribbon-host {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    background: var(--bg-card);
    /* No hard outline — soft backing only, so the set list reads as part of one
       calm column instead of a second outlined widget below the hero. */
    border-radius: var(--radius-card);
    padding: var(--space-3) var(--space-4);
  }
  .hdr h2 {
    /* The sets ribbon is the star of the page — its title leads the eye at a
       clear headline weight (22px/600), a visible jump above the body/label
       tiers below it. (Design brief 2026-07-08: story first, biggest weight.)
       Canonical scale token — no parallel scale. */
    font: var(--type-title-ribbon);
    color: var(--text-primary);
    margin: 0;
    letter-spacing: -0.01em;
  }
  .hdr .sub {
    /* Anchor subline — which swim + how many sets. 15px floor per the VP
       gate (Scott reads at a glance; caption-size would be too small).
       Brighter than --text-muted so it stays legible over the gradient. */
    font-size: 15px;
    line-height: 1.3;
    color: #C5C6CC;
    margin: 2px 0 0;
  }
  /* Static, always-visible explainer (never hover-only) — tells the reader in
     plain language how the ribbon decides where one set ends and the next
     begins. 15px floor per the VP gate. */
  .caption {
    font-size: 15px;
    line-height: 1.3;
    color: #B8B9BF;
    margin: 0;
  }
</style>
